import dayjs from 'dayjs';

export interface RawDataPoint {
  timestamp: string;
  value: number | null | undefined;
}

export type ChartDataPoint = [number, number | null];

export type ChartData = ChartDataPoint[];

export type ValidDataPoint = [number, number];

export function transformDataToChartFormat(
  data: RawDataPoint[] | null | undefined,
): ChartData {
  if (!data || data.length === 0) {
    return [];
  }

  return data.map(
    (point): ChartDataPoint => [
      dayjs.utc(point.timestamp).valueOf(),
      point.value === null || point.value === undefined ? null : point.value,
    ],
  );
}

export function calculateChartYAxisMax(chartData: ChartData): number {
  if (chartData.length === 0) {
    return 120;
  }

  const validValues = chartData
    .map((point) => point[1])
    .filter((value): value is number => value !== null && value !== undefined);

  if (validValues.length === 0) {
    return 120;
  }

  const maxValue = Math.max(...validValues);
  const paddedMax = Math.ceil(maxValue * 1.1);

  return Math.max(120, paddedMax);
}

export function findClosestDataPoint(
  chartData: ChartData,
  targetTimestamp: string | null | undefined,
): ValidDataPoint | null {
  if (!targetTimestamp || chartData.length === 0) {
    return null;
  }

  const targetTime = dayjs.utc(targetTimestamp).valueOf();
  let closestPoint: ValidDataPoint | null = null;
  let minDiff = Infinity;

  for (const point of chartData) {
    if (point[1] === null || point[1] === undefined) {
      continue;
    }

    const timeDiff = Math.abs(point[0] - targetTime);
    if (timeDiff < minDiff) {
      minDiff = timeDiff;
      closestPoint = point as ValidDataPoint;
    }
  }

  return closestPoint;
}

export function getCurrentTemperatureFromData(
  data: RawDataPoint[] | null | undefined,
  fallbackValue: number | null | undefined,
): number | null | undefined {
  if (data && data.length > 0) {
    const latestDataPoint = data[data.length - 1];
    return latestDataPoint.value;
  }

  return fallbackValue;
}
